import DocumentViewerModal from "components/DocumentViewer";
import { StatusChip } from "components/StatusChip";
import { useAuth } from "contexts/AuthContext";
import { useTranslation } from "contexts/i18nContext";
import { useHasRole } from "contexts/AuthContext";
import { useEffect, useState } from "react";
import { fetchAdminUnitDetail } from "services/advocateComplaintService";
import { AdminUnit, Investigation } from "types";
import { COMPLIANT_STATUS, FEEDBACK_TYPE } from "utils/constantValues";
import { isInvestigationAllowed } from "utils/IsInvestigationAllowed";
import { ROLES } from "utils/roles";
import AddComplaintDecisionModal from "./AddComplaintDecisionModal";
import AddDecisionModal from "./AddDecisionModal";
import AddEvaluationModal from "./AddEvaluationModal";
import AddFeedbackModal from "./AddFeedbackModal";
import AddFurtherInvestigationModal from "./AddFurtherInvestigationModal";
import ViewEvaluationModal from "./ViewEvaluationModal";

export default function DecisionCard({
  complaintData,
  investigationData,
  allInvestigationData,
  onInvestigationRefresh,
  onComplaintsRefresh,
}: any) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canCreateInvestigationDecision = useHasRole([
    ROLES.INSPECTION_PROSECUTOR,
  ]);
  const canCreateInvestigationFeedback = useHasRole([
    ROLES.INSPECTION_COORDINATOR,
    ROLES.INSPECTION_DIRECTOR,
  ]);

  const [isComplaintOpen, setIsComplaintOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [isEvaluationFormOpen, setIsEvaluationFormOpen] = useState(false);
  const [isEvaluationViewOpen, setIsEvaluationViewOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isFurtherInvestigationOpen, setIsFurtherInvestigationOpen] =
    useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  const [adminUnitData, setAdminUnitData] = useState<AdminUnit>();

  useEffect(() => {
    const loadAdminUnitDetail = async () => {
      try {
        const adminUnitDetail = await fetchAdminUnitDetail(
          user?.admin_unit,
          user?.access,
        );
        setAdminUnitData(adminUnitDetail);
      } catch (err) {
        console.error("Failed to load admin unit detail:", err);
      }
    };
    loadAdminUnitDetail();
  }, [user]);

  const chargeDocuments = (
    investigationData?.investigation_documents ||
    investigationData?.documents ||
    []
  )?.filter((i: any) => i?.document_type === "charge_document");

  return (
    <div className="mb-8">
      {/* Public Prosecutor Decision Header */}
      <div className="mb-4">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full text-left focus:outline-none focus:ring-2 focus:ring-red-500 rounded"
        >
          <h2 className="text-lg font-semibold text-gray-800 border-l-4 border-red-600 pl-3 py-1 flex items-center justify-between">
            <span>{t("publicProsecutorDecision")}</span>
            <svg
              className={`w-5 h-5 text-gray-600 transform transition-transform ${isExpanded ? "rotate-180" : "rotate-0"}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </h2>
        </button>
      </div>

      {/* Main Decision Container */}
      {isExpanded && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-4">
          {/* Assigned Prosecutor Section */}
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <img
                    src={require("../../../images/avatar.png")}
                    alt="avatar"
                    className="w-10 h-10 rounded-full border-2 border-white shadow-sm"
                  />
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm">
                    {complaintData?.assigned_prosecutor || t("unassigned")}
                  </p>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-600">{t("assignedDate")}:</span>
                    <span className="font-medium text-gray-800">
                      {complaintData?.assigned_date || "-"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Reassign Button */}
              {canCreateInvestigationFeedback &&
                user?.admin_unit === complaintData?.admin_unit_id &&
                complaintData?.status !== COMPLIANT_STATUS.CLOSED &&
                complaintData?.status !== COMPLIANT_STATUS.TERMINATED &&
                complaintData?.status !==
                  COMPLIANT_STATUS.MORE_INFORMATION_REQUESTED && (
                  <button
                    className="px-4 py-2 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 transition-colors shadow-sm"
                    type="submit"
                    onClick={() => setIsComplaintOpen(true)}
                  >
                    {t("reassignCase")}
                  </button>
                )}
            </div>
          </div>

          {/* Investigation Decisions Timeline */}
          <div className="p-5 space-y-6">
            {allInvestigationData
              ?.sort(
                (a: any, b: any) =>
                  new Date(b?.createdAt ?? "").getTime() -
                  new Date(a?.createdAt ?? "").getTime(),
              )
              ?.slice(0, showAll ? undefined : 1)
              ?.map((obj: Investigation, index: number) => {
                const filtered =
                  obj?.investigation_documents &&
                  obj?.investigation_documents?.filter(
                    (a: any) => a?.document_type === "prosecutor_decision",
                  );
                // const workSpeedEvaluation = (obj as any)?.work_speed_evaluation;
                // const workQualityEvaluation = (obj as any)
                //   ?.work_quality_evaluation;
                // const workLoadEvaluation = (obj as any)?.work_load_evaluation;
                // const hasWorkEvaluation =
                //   workSpeedEvaluation !== undefined ||
                //   workQualityEvaluation !== undefined ||
                //   workLoadEvaluation !== undefined;
                return (
                  <div key={index} className="relative">
                    {/* Timeline connector */}
                    {index < (allInvestigationData?.length ?? 0) - 1 && (
                      <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gray-200"></div>
                    )}

                    <div className="flex gap-4">
                      {/* Timeline dot */}
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 flex items-center justify-center bg-red-100 border-2 border-red-300 rounded-full">
                          <svg
                            className="w-5 h-5 text-red-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>

                        {canCreateInvestigationFeedback &&
                          investigationData &&
                          investigationData?.evaluations?.length === 0 &&
                          investigationData.prosecutor_decision_type &&
                          (investigationData?.feedback_status ===
                            FEEDBACK_TYPE?.REVISE ||
                            !investigationData?.feedback_status) && (
                            <div
                              className="flex flex-col items-center justify-center gap-2 cursor-pointer mt-3 w-12"
                              onClick={() => setIsEvaluationFormOpen(true)}
                            >
                              <div className="w-12 h-12 flex items-center justify-center gap-2 bg-gradient-to-r from-red-100 to-red-200 border-2 border-red-300 rounded-full shadow hover:from-red-200 hover:to-red-300 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-red-400">
                                <span className="flex items-center justify-center w-8 h-8  border-red-200 rounded-full">
                                  <svg
                                    className="w-5 h-5 text-red-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                    />
                                  </svg>
                                </span>
                              </div>
                              <span className="font-thin text-red-700 text-xs text-center">
                                {t("addEvaluation")}
                              </span>
                            </div>
                          )}

                        {canCreateInvestigationFeedback &&
                          investigationData &&
                          investigationData?.evaluations?.length > 0 &&
                          investigationData.prosecutor_decision_type &&
                          (investigationData?.feedback_status ===
                            FEEDBACK_TYPE?.REVISE ||
                            !investigationData?.feedback_status) && (
                            <div
                              className="flex flex-col items-center justify-center gap-2 cursor-pointer mt-3 w-12"
                              onClick={() => setIsEvaluationViewOpen(true)}
                            >
                              <div className="w-12 h-12 flex items-center justify-center gap-2 bg-gradient-to-r from-red-100 to-red-200 border-2 border-red-300 rounded-full shadow hover:from-red-200 hover:to-red-300 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-red-400">
                                <span className="flex items-center justify-center w-8 h-8  border-red-200 rounded-full">
                                  <svg
                                    className="w-5 h-5 text-red-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                    />
                                  </svg>
                                </span>
                              </div>
                              <span className="font-thin text-red-700 text-xs text-center">
                                {t("viewEvaluation")}
                              </span>
                            </div>
                          )}
                      </div>

                      {/* Decision Content */}
                      <div className="flex-1">
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <h4 className="font-semibold text-gray-800 text-sm">
                                {t("prosecutorDecision")}
                              </h4>
                              <div className="flex items-center gap-3 mt-1">
                                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                                  {obj.prosecutor_decision_type ||
                                    t("decision")}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {obj.investigation_start_date || t("noDate")}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Decision Summary */}
                          {obj?.prosecutor_decision_summary && (
                            <div className="mb-4">
                              <p className="text-sm text-gray-700 leading-relaxed bg-white p-3 rounded border border-gray-100">
                                {obj?.prosecutor_decision_summary}
                              </p>
                            </div>
                          )}

                          {/* {hasWorkEvaluation && (
                            <div className="mb-4">
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                                Work Evaluation
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <div className="bg-white p-3 rounded border border-gray-100">
                                  <p className="text-xs text-gray-500">
                                    Work Speed
                                  </p>
                                  <p className="text-sm font-semibold text-gray-800">
                                    {workSpeedEvaluation ?? "-"}
                                  </p>
                                </div>
                                <div className="bg-white p-3 rounded border border-gray-100">
                                  <p className="text-xs text-gray-500">
                                    Work Quality
                                  </p>
                                  <p className="text-sm font-semibold text-gray-800">
                                    {workQualityEvaluation ?? "-"}
                                  </p>
                                </div>
                                <div className="bg-white p-3 rounded border border-gray-100">
                                  <p className="text-xs text-gray-500">
                                    Work Load
                                  </p>
                                  <p className="text-sm font-semibold text-gray-800">
                                    {workLoadEvaluation ?? "-"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )} */}

                          {/* Decision Documents */}
                          {filtered && filtered.length > 0 && (
                            <div className="mb-4">
                              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                                {t("decisionDocuments")}
                              </p>
                              <DocumentViewerModal
                                documents={filtered as any}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                              />
                            </div>
                          )}

                          {/* Director Feedback */}
                          {obj?.director_feedbacks &&
                            obj?.director_feedbacks?.length > 0 && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <svg
                                      className="w-4 h-4 text-gray-500"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                                      />
                                    </svg>
                                    <p className="text-sm font-medium text-gray-700">
                                      {t("feedbackType")}
                                    </p>
                                  </div>
                                  <StatusChip
                                    status={obj?.feedback_status || ""}
                                  />
                                </div>
                                <div className="bg-red-50 border border-red-100 rounded-lg p-4">
                                  <p className="text-sm text-gray-800 leading-relaxed">
                                    {obj?.director_feedbacks[0]?.comment || ""}
                                  </p>
                                </div>
                              </div>
                            )}

                          {obj?.further_investigations?.map((f: any) => {
                            return (
                              <div key={f?.id}>
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                                  <div className="flex justify-between items-start mb-3">
                                    <div>
                                      <h4 className="font-semibold text-gray-800 text-sm">
                                        {t("prosecutorDecisionSummary")}
                                      </h4>
                                      <div className="flex items-center gap-3 mt-1">
                                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-normal bg-blue-100 text-blue-800 border border-blue-200">
                                          {f?.comment}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {f?.createdAt
                                            ? new Date(
                                                f.createdAt,
                                              ).toLocaleDateString()
                                            : t("noDate")}
                                        </span>
                                      </div>

                                      {f?.documents &&
                                        f?.documents.length > 0 && (
                                          <div className="mb-4">
                                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                                              {t("decisionDocuments")}
                                            </p>
                                            <DocumentViewerModal
                                              documents={f?.documents}
                                              baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                                            />
                                          </div>
                                        )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

            {/* Show All/Hide Button */}
            {allInvestigationData?.length > 1 && (
              <div className="text-center pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowAll((prev) => !prev)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d={showAll ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"}
                    />
                  </svg>
                  {showAll ? t("hideFeedbacks") : t("showAllFeedbacks")}
                </button>
              </div>
            )}

            {/* Add Feedback Button */}
            {canCreateInvestigationFeedback &&
              investigationData &&
              investigationData.prosecutor_decision_type &&
              (investigationData?.feedback_status === FEEDBACK_TYPE?.REVISE ||
                !investigationData?.feedback_status) &&
              chargeDocuments.length === 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <button
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-red-700 to-red-600 hover:from-red-800 hover:to-red-700 text-white font-medium rounded-lg transition-colors shadow-sm"
                    onClick={() => setIsFeedbackOpen(true)}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                      />
                    </svg>
                    {t("addFeedback")}
                  </button>
                </div>
              )}

            {canCreateInvestigationFeedback &&
              adminUnitData?.parent_unit &&
              complaintData?.status ===
                COMPLIANT_STATUS.MORE_INFORMATION_REQUESTED && (
                <div className="flex justify-end space-x-3">
                  <div className="flex-1"></div>
                  <button
                    className="px-6 py-2.5 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 transition-colors shadow-sm flex items-center gap-2"
                    type="submit"
                    onClick={() => setIsFurtherInvestigationOpen(true)}
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    {t("addFurtherInvestigation")}
                  </button>
                </div>
              )}
          </div>
        </div>
      )}

      {/* Add Decision Button */}
      {canCreateInvestigationDecision &&
        complaintData?.status !== COMPLIANT_STATUS.TERMINATED &&
        complaintData?.status !== COMPLIANT_STATUS.CLOSED &&
        user?.admin_unit === complaintData?.admin_unit_id &&
        isInvestigationAllowed(
          complaintData?.offence_type,
          complaintData?.assigned_date,
        ) &&
        (!investigationData ||
          (investigationData &&
            ((investigationData?.feedback_status_charge_docu ===
              FEEDBACK_TYPE?.REVISE &&
              investigationData?.status === "Back to Prosecutor") ||
              (investigationData?.prosecutor_decision_type ===
                "out of jurisdiction forward to zone" &&
                investigationData?.status === "Under investigation") ||
              (investigationData?.prosecutor_decision_type ===
                "Forwarded to zone for further investigation" &&
                investigationData?.status === "Under investigation")))) && (
          <div className="flex justify-end space-x-3">
            <div className="flex-1"></div>
            <button
              className="px-6 py-2.5 bg-red-700 text-white text-sm font-medium rounded-md hover:bg-red-800 transition-colors shadow-sm flex items-center gap-2"
              type="submit"
              onClick={() => setIsOpen(true)}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {t("addDecision")}
            </button>
          </div>
        )}

      {/* Modals */}
      <AddDecisionModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        complaintData={complaintData}
        onRefresh={onInvestigationRefresh}
        addMoreDecision={adminUnitData?.parent_unit ? false : true}
      />

      <AddEvaluationModal
        isOpen={isEvaluationFormOpen}
        setIsOpen={setIsEvaluationFormOpen}
        onRefresh={onInvestigationRefresh}
        investgiation={investigationData}
      />
      <ViewEvaluationModal
        isOpen={isEvaluationViewOpen}
        setIsOpen={setIsEvaluationViewOpen}
        evaluations={investigationData?.evaluations}
      />

      <AddComplaintDecisionModal
        isOpen={isComplaintOpen}
        setIsOpen={setIsComplaintOpen}
        complaintData={complaintData}
        onRefresh={onComplaintsRefresh}
        reassing={true}
      />
      {investigationData && (
        <AddFeedbackModal
          isOpen={isFeedbackOpen}
          setIsOpen={setIsFeedbackOpen}
          complaintData={complaintData}
          investigationData={investigationData}
          onRefresh={onInvestigationRefresh}
        />
      )}
      {investigationData && adminUnitData && (
        <AddFurtherInvestigationModal
          adminUnitData={adminUnitData}
          isOpen={isFurtherInvestigationOpen}
          setIsOpen={setIsFurtherInvestigationOpen}
          complaintData={complaintData}
          investigationData={investigationData}
          onRefresh={onInvestigationRefresh}
        />
      )}
    </div>
  );
}
